import { useCallback, useEffect, useMemo, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { SubmitHandler } from "react-hook-form";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";

import { FORM_ERROR_DELAY_IN_MS } from "@covertree/consts";
import { useFormWithNotification } from "@covertree/hooks";
import { Form, FormTextField, SubmitButton } from "@covertree/ui";
import { AddressAutocompletePrecisely } from "@covertree/features";
import {
  ClaimsCheckForm,
  claimsCheckValidation,
} from "@covertree/new-business-forms";
import {
  selectPolicyholderPreviousAddress,
  selectPriorInsuranceDetails,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";
import { InformationBox } from "@covertree/ui/content";
import { LegalDocumentLink } from "@covertree/ui/link";

interface ClaimsCheckProps {
  handleClaimsCheck: SubmitHandler<ClaimsCheckForm>;
  isD2C?: boolean;
}

export default function ClaimsCheck({
  handleClaimsCheck,
  isD2C,
}: ClaimsCheckProps) {
  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false);
  const insuranceDetails = useNewBusinessSelector(selectPriorInsuranceDetails);
  const previousAddress = useNewBusinessSelector(
    selectPolicyholderPreviousAddress
  );

  const defaultAddress = useMemo((): ClaimsCheckForm | undefined => {
    if (!previousAddress) {
      return;
    }

    const [addressNumber, ...streetName] =
      previousAddress.streetAddress.split(" ");
    return {
      address: {
        label: `${previousAddress.streetAddress}${previousAddress.unitNumber ? `, ${previousAddress.unitNumber},` : ""} ${previousAddress.city}, ${previousAddress.state} ${previousAddress.zipCode}`,
        value: {
          addressLastLine: `${previousAddress.city}, ${previousAddress.state} ${previousAddress.zipCode}`,
          addressNumber,
          city: previousAddress.city,
          country: previousAddress.countryCode,
          formattedAddress: `${previousAddress.streetAddress}${previousAddress.unitNumber ? `, ${previousAddress.unitNumber}` : ""} ${previousAddress.city}, ${previousAddress.state} ${previousAddress.zipCode}`,
          mainAddressLine: previousAddress.streetAddress,
          postCode: previousAddress.zipCode,
          state: previousAddress.state,
          streetName: streetName.join(" "),
          lotUnit: previousAddress.unitNumber,
        },
      },
      lotUnit: previousAddress.unitNumber,
    };
  }, [previousAddress, insuranceDetails]);

  const form = useFormWithNotification({
    mode: "all",
    reValidateMode: "onSubmit",
    delayError: FORM_ERROR_DELAY_IN_MS,
    defaultValues: defaultAddress,
    resolver: yupResolver(claimsCheckValidation),
  });

  const {
    control,
    handleSubmit: submit,
    formState: { isValid },
    watch,
    setValue,
  } = form;

  const onSubmit = useCallback<SubmitHandler<ClaimsCheckForm>>(
    async (data) => {
      setIsFormSubmitting(true);
      try {
        await handleClaimsCheck(data);
      } finally {
        setIsFormSubmitting(false);
      }
    },
    [handleClaimsCheck]
  );

  const address = watch("address");

  useEffect(() => {
    if (address?.value) {
      setValue("lotUnit", address.value.lotUnit ?? "");
    }
  }, [address, setValue]);

  return (
    <>
      <Box sx={isD2C ? { pt: 4 } : {}}>
        <Form onSubmit={submit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <AddressAutocompletePrecisely
                id="address-autocomplete-input"
                dataTestId="autocomplete-input"
                name="address"
                label="What was your primary address for the last year?"
                control={control}
                tooltip="US addresses only"
                disabled={isFormSubmitting}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormTextField
                id="unit-input"
                dataTestId="unit-input"
                name="lotUnit"
                label="Unit / Lot#"
                control={control}
                optional
                disabled={isFormSubmitting}
              />
            </Grid>
          </Grid>
          {isD2C && (
            <InformationBox mt={2}>
              I certify that I am, or was, the legal owner of the listed address
              and I will use any information received from CoverTree relating to
              credit or claim history only for the purpose of securing insurance
              coverage through CoverTree. I agree that any other use is
              prohibited and understand that CoverTree&apos;s use of my
              information is subject to CoverTree&apos;s{" "}
              <LegalDocumentLink document="PrivacyPolicy" underline="hover" />{" "}
              and <LegalDocumentLink document="TermsOfUse" underline="hover" />.
            </InformationBox>
          )}
          <SubmitButton
            dataTestId="subscribe-button"
            width={200}
            loading={isFormSubmitting}
            disabled={isFormSubmitting || !isValid}
            mt={2}>
            Run check
          </SubmitButton>
        </Form>
      </Box>
    </>
  );
}

export { ClaimsCheck };
